import type { FC } from 'react';
import { useState, useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Box,
  Typography,
  TableSortLabel
} from '@mui/material';
import { reportService } from '@services/reportService';
import type { IncompleteTimesheetRow } from '@/types';
import toast from 'react-hot-toast';

const IncompleteTimesheetsTable: FC = () => {
  const [data, setData] = useState<IncompleteTimesheetRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const response = await reportService.getIncompleteTimesheets();
      setData(response.rows || []);
    } catch (error) {
      console.error('Error loading incomplete timesheets:', error);
      toast.error('Failed to load incomplete timesheets data');
    } finally {
      setIsLoading(false);
    }
  };

  const columns = useMemo<ColumnDef<IncompleteTimesheetRow>[]>(
    () => [
      {
        accessorKey: 'employee',
        header: 'Employee',
      },
      {
        accessorKey: 'date',
        header: 'Week Starting',
        cell: (info) => {
          const dateStr = info.getValue() as string;
          // API returns "YYYY/MM/DD" format, convert to "MM/DD/YYYY"
          try {
            const [year, month, day] = dateStr.split('/');
            return `${month}/${day}/${year}`;
          } catch {
            return dateStr;
          }
        },
      },
      {
        accessorKey: 'total_hours',
        header: 'Total Hours',
        cell: (info) => (info.getValue() as number).toFixed(2),
      },
      {
        accessorKey: 'billable_hours',
        header: 'Billable Hours',
        cell: (info) => (info.getValue() as number).toFixed(2),
      },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Typography textAlign="center" color="text.secondary" sx={{ mt: 3 }}>
        All timesheets are complete!
      </Typography>
    );
  }

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell key={header.id}>
                    {header.isPlaceholder ? null : (
                      <TableSortLabel
                        active={!!header.column.getIsSorted()}
                        direction={header.column.getIsSorted() === 'asc' ? 'asc' : 'desc'}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} hover>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={data.length}
        page={table.getState().pagination.pageIndex}
        onPageChange={(_event, newPage) => table.setPageIndex(newPage)}
        rowsPerPage={table.getState().pagination.pageSize}
        onRowsPerPageChange={(event) => table.setPageSize(Number(event.target.value))}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />
    </Paper>
  );
};

export default IncompleteTimesheetsTable;
